// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Flex, Box, useSystem } from 'react-system';
import {
  createDescriptor,
  useRouterChanging,
  useRouter,
} from '../../controls/router';
import { Center } from '../../controls/Center';
import { useLocale } from '../../controls/locale';
import { FixedPagination } from '../../controls/FixedPagination';
import { useScrollTopOnRouteChangeTimeout } from '../../hooks/scroll_top_on_route_change_timeout';
import { SectionHeading } from '../../controls/section-heading';
import { useTheme } from '../../theme';
import {
  DevelopmentSummary,
  useSummaryLoadingStyle,
} from './DevelopmentSummary';
import { DevelopmentsSort } from './DevelopmentsSort';
import { LocationFilter } from './Filters/LocationFilter';
import { pageDescriptor } from './sharedDescriptors';
import type { Developments_root$key } from './__generated__/Developments_root.graphql';

const developmentsDescriptor = createDescriptor(pageDescriptor);

type Props = {|
  root: Developments_root$key,
|};

export const ITEMS_PER_PAGE = 45;

export const Developments = (props: Props): React.Node => {
  const { t } = useLocale();
  const { media } = useSystem();
  const { text } = useTheme();

  const [developmentsState, setDevelopmentsState, flush] =
    developmentsDescriptor.useState();

  const devRef = React.useRef(null);
  const changingTo = useRouterChanging();
  const router = useRouter();

  const scrollToTopTimeout = useScrollTopOnRouteChangeTimeout(devRef);
  const loadingStyle = useSummaryLoadingStyle();

  const root = useFragment(
    graphql`
      fragment Developments_root on Query {
        ...LocationFilter_root
        developmentProducts(
          page: $page
          sortBy: $sortBy
          sortDirection: $sortDirection
          filters: $filters
        ) {
          totalCount
          edges {
            node {
              ...DevelopmentSummary_development
            }
          }
        }
      }
    `,
    props.root,
  );

  const pagesCount = Math.ceil(
    (root.developmentProducts.totalCount ?? 0) / ITEMS_PER_PAGE,
  );

  const page = Math.min(Math.max(0, developmentsState.page), pagesCount - 1);

  const pageItems = root.developmentProducts.edges ?? [];

  const fromNumber = page * ITEMS_PER_PAGE + 1;
  const toNumber =
    fromNumber + ITEMS_PER_PAGE - 1 <= root.developmentProducts.totalCount
      ? fromNumber + ITEMS_PER_PAGE - 1
      : root.developmentProducts.totalCount;

  const totalRounded =
    root.developmentProducts.totalCount > 100
      ? Math.floor(root.developmentProducts.totalCount / 100) * 100
      : Math.floor(root.developmentProducts.totalCount / 10) * 10;

  return (
    <>
      <Box pt={6} />
      <SectionHeading align="center">
        {t('realEstateDevelopments')}
      </SectionHeading>

      <div ref={devRef} />
      <Center>
        <Flex my={4} mx={-2} alignItems={'center'}>
          <Box mr={2}>
            <LocationFilter root={root} title={t('location')} />
          </Box>

          <Box flexGrow={1} />

          <Box mr={3} css={media({ display: ['none', 'block'] })}>
            <span
              css={{
                fontWeight: text.font.medium,
              }}
            >
              {t('sortBy')}:
            </span>
          </Box>
          <Box>
            <DevelopmentsSort />
          </Box>
        </Flex>

        <div
          css={changingTo?.match.pathname === router.pathname && loadingStyle}
        >
          <Flex flexWrap="wrap" mx={-3} mt={-2}>
            {pageItems.map((edge, i) => {
              if (edge == null || edge.node == null) {
                return null;
              }
              // reuse keys on page change
              return (
                <Box width={[1, 1 / 2, 1 / 3]} p={2} key={i}>
                  <DevelopmentSummary development={edge.node} />
                </Box>
              );
            })}
          </Flex>
        </div>

        <Flex justifyContent="center" my={3}>
          {pagesCount > 1 && (
            <FixedPagination
              page={page}
              count={pagesCount}
              onPageChange={pageNum => {
                setDevelopmentsState({ page: pageNum });
                flush();
                scrollToTopTimeout();
              }}
            />
          )}
        </Flex>

        {pagesCount > 1 && (
          <Flex justifyContent="center" my={3} css={text.body1}>
            {fromNumber}-{toNumber}{' '}
            {t('ofMoreThanRentals', { count: totalRounded })}
          </Flex>
        )}
      </Center>
    </>
  );
};
